import * as Sentry from "@sentry/vue"
import type { App } from "vue"
import type { Router } from "vue-router"
import { isNavigationFailure } from "vue-router"
import errorTypeConst from "@common_lib/base/constants/errorTypeConst"
import { isChunkLoadError, scheduleChunkErrorReload } from "@/utils/chunkLoad"
import {
  bucketNavigationGap,
  getActiveStorm,
  isCorruptionMessage,
  recordNavigation,
  recordVueError
} from "@/services/vdomCorruptionTracker"

const IS_DEV = import.meta.env.MODE === "dev" || import.meta.env.MODE === "dev-nginx"
const IS_PROD = import.meta.env.MODE === "prod"

const PROD_CUSTOM_CONFIG = {
  sampleRate: 0.005,
  tracesSampleRate: 0,
  // 10 covered only ~3s of the vdom error storms (one console-error + several
  // xhr crumbs per ~1.2s poll cycle); 50 keeps the click/navigation that
  // precedes a crash visible. Payload cost only applies to sampled events.
  maxBreadcrumbs: 50,
  normalizeDepth: 3
}

const UAT_CUSTOM_CONFIG = {
  sampleRate: 1,
  tracesSampleRate: 1,
  maxBreadcrumbs: 100,
  normalizeDepth: 5
}

const CUSTOM_CONFIG = IS_PROD ? PROD_CUSTOM_CONFIG : UAT_CUSTOM_CONFIG

const IGNORE_ERRORS = [
  "error occurred",
  "request canceled",
  "network error",
  /unable to preload/i,
  "locale messages",
  /the play\(\) request was interrupted/i, // jbl.js media error
  "AxiosError: Request aborted",
  "AxiosError: Network Error",
  // 410 Gone is an expected backend business signal (session / resource gone),
  // handled per-endpoint in api.ts — drop the Sentry noise without changing flow.
  "Request failed with status code 410",
  // Native fetch failures from network drops, page-unload aborts, or third-party scripts.
  // Anchored to avoid masking the chunk-load variant ("Failed to fetch dynamically imported module: ...").
  /^(?:TypeError: )?Failed to fetch$/i,
  "mini game webSocket is not connected",
  /(?:Error invoking \w+: )?Method not found/i, // JBL WebSocket method not found
  "JBLWebSocket is not defined",
  /Can't find variable: JBLWebSocket/i,
  "cordova is not defined",
  /Can't find variable: cordova/i,
  // Third-party browser-injected scripts (Samsung Internet, X Browser, etc.)
  "swbrowser is not defined",
  /Can't find variable: swbrowser/i,
  "xbrowser is not defined",
  /Can't find variable: xbrowser/i,
  // Injected third-party / in-app WebView chart script; `plot` exists nowhere
  // in this app, so the ReferenceError is never ours (onerror, anonymous frame).
  "plot is not defined",
  /Can't find variable: plot/i,
  // Broken JS injected by APK-wrapper WebViews via evaluateJavascript: the only
  // frame is the page URL itself at line 1, and 91% of affected users are on
  // Chrome Mobile WebView / VivoBrowser. A bare `else` cannot come from our
  // Vite bundle (it would fail the build). Deliberately narrow — the deploy
  // signal "Unexpected token '<'" (HTML served as JS) must keep reporting.
  // (9WICKET-CLIENT-8X)
  "Unexpected token 'else'",
  // Benign browser warnings: ResizeObserver aborts a layout loop without
  // breaking functionality; W3C and Chromium classify these as safe to ignore.
  /ResizeObserver loop limit exceeded/,
  /ResizeObserver loop completed with undelivered notifications/
]

// 建立排除的錯誤條件列表（status code 和 message 都納入比對）
const EXCLUDED_ERROR_MATCHERS = Object.values(errorTypeConst)
  .filter(errorInfo => errorInfo.excludeFromSentry)
  .flatMap(errorInfo => [errorInfo.status, errorInfo.message].filter(Boolean) as string[])

// Status codes of expected business states that must never reach Sentry.
const EXCLUDED_ERROR_STATUSES = new Set(
  Object.values(errorTypeConst)
    .filter(errorInfo => errorInfo.excludeFromSentry)
    .map(errorInfo => String(errorInfo.status))
)

// 9999 (FAIL) is the backend's generic business-failure status — common and
// surfaced to the user by each caller's own UX flow, so it is Sentry noise
// (9WICKET-CLIENT-X / 2QW). Excluded ONLY via the exact object-status check:
// it must never join the substring-based EXCLUDED_ERROR_MATCHERS, where "9999"
// would false-positive on unrelated messages (e.g. bet limits like 9999999999).
EXCLUDED_ERROR_STATUSES.add(String(errorTypeConst.FAIL.status))

// Crypto-wallet browser extensions (EIP-1193 providers) reject with
// {code, message} objects such as code 4001 "wallet must has at least one
// account"; this app has no web3 code, so those rejections are never ours.
// The message only lives on the rejected object (the event text is just
// "Object captured as promise rejection with keys: code, message"), so the
// filter must inspect hint.originalException. (9WICKET-CLIENT-2QB)
const isWalletExtensionReject = (value: unknown): boolean => {
  if (typeof value !== "object" || value === null || !("message" in value)) return false
  const { message } = value
  return (
    typeof message === "string" &&
    message.includes("wallet must has at least one account")
  )
}

// api.ts rejects failure responses (system maintenance, IP block, 9999 FAIL, ...)
// as ApiStatusError, whose exception value is the backend's status_msg rather than
// the status code, so the string-based EXCLUDED_ERROR_MATCHERS can't catch them.
// Inspect the rejected value's status field directly.
const isExcludedStatusReject = (value: unknown): boolean => {
  if (typeof value !== "object" || value === null || !("status" in value)) return false
  const { status } = value
  return typeof status === "string" && EXCLUDED_ERROR_STATUSES.has(status)
}

const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /jbl\.js/i,
  "player-min.js",
  /analysis\.qn32\.com/i,
  /akamaized\.net/i, // Betradar LMT widget external errors
  /freshchat\.com/i, // FreshChat widget JSON parse errors
  /ucbrowser_script\.js/i, // UCBrowser injected script errors
  // Firefox labels extension-eval'd code as "<url> line N > injectedScript";
  // those frames are never our bundle (9WICKET-CLIENT-2MC).
  /injectedScript/
]

const errorCounts = new Map()
const ERROR_THRESHOLD = 5

// Network Information API is Chromium-only and absent from lib.dom, so the
// property is declared optional here; optional chaining doubles as feature
// detection (Mobile Safari / Firefox report "unsupported").
declare global {
  interface Navigator {
    connection?: { effectiveType?: string }
  }
}

const getEffectiveConnectionType = (): string =>
  navigator.connection?.effectiveType ?? "unsupported"

const initSentry = ({ app, router }: { app: App; router: Router }) => {
  if (IS_DEV) return

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    // v10 no longer infers user IP unless this is set; keep it on so Sentry
    // events retain country/region data used for issue triage.
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration({ router })],
    denyUrls: DENY_URLS,
    ignoreErrors: IGNORE_ERRORS,
    beforeSend(event, hint) {
      // vue-router rejects the navigation promise on next(false) /
      // next({...}) by spec; these are flow-control signals, not bugs.
      if (isNavigationFailure(hint?.originalException)) {
        return null
      }

      // Expected business states rejected as raw objects (system maintenance,
      // IP block, ...) that the message-based filter below can't see.
      // (9WICKET-CLIENT-2P9 / 2P8 / 2PG "System is under Maintenance")
      if (isExcludedStatusReject(hint?.originalException)) {
        return null
      }

      if (isWalletExtensionReject(hint?.originalException)) {
        return null
      }

      if (!event.exception?.values) return event

      const errorMessage = event.exception.values[0].value

      if (!errorMessage) return event

      // 檢查是否為需要排除的錯誤（比對 status code 和 message）
      const shouldExclude = EXCLUDED_ERROR_MATCHERS.some((matcher: string) =>
        errorMessage.includes(matcher)
      )

      if (shouldExclude) {
        return null
      }

      // Chunk load errors are caused by deploy cache invalidation. Always drop these
      // events: router.onError already schedules a reload, and the same error also
      // reaches here via Vue's errorHandler, where the sessionStorage flag may already
      // be set — checking the reload return value would let the duplicate slip through.
      if (isChunkLoadError(errorMessage)) {
        scheduleChunkErrorReload(errorMessage)
        return null
      }

      // Chrome Mobile iOS reports phantom `Maximum call stack size exceeded` from
      // injected content scripts (translate, password manager, ad blockers) with
      // meaningless frames (`undefined:N:N`, or two-function loops inlined in the
      // page URL). Persists across releases since 2025-07; unactionable without
      // real frames. Scoped to Chrome iOS (CriOS) so genuine recursion bugs on
      // other browsers still surface. Detection must use the user agent: the
      // previous `event.contexts.browser` check never matched because browser
      // context is ingest-side enrichment that does not exist in client-side
      // beforeSend, which left these events flowing (9WICKET-CLIENT-8G/2QV/2QS).
      if (
        errorMessage.startsWith("Maximum call stack size exceeded") &&
        navigator.userAgent.includes("CriOS")
      ) {
        return null
      }

      // Tag vdom-corruption storm events (9WICKET-CLIENT-2HT / 2JH / 2K3 / 2J0
      // / 2K1) with what the tracker saw when the storm STARTED — the sampled
      // event itself usually lands mid-storm, minutes after the trigger has
      // left the breadcrumb window. Context keys stay flat so normalizeDepth: 3
      // cannot truncate them.
      if (isCorruptionMessage(errorMessage)) {
        const storm = getActiveStorm()
        // eslint-disable-next-line no-param-reassign -- mutating the outgoing event is Sentry's documented beforeSend contract
        event.tags = {
          ...event.tags,
          "vdom.storm": storm?.phase ?? "untracked",
          "vdom.nav_before_storm": bucketNavigationGap(storm?.navigation?.msBeforeStorm),
          "vdom.preceding_vue_error": storm?.precedingError ? "true" : "false",
          // slow-2g / 2g / 3g / 4g — if corruption skews slower than the
          // project's baseline errors, slow networks amplify the race window.
          "vdom.network": getEffectiveConnectionType()
        }
        if (storm) {
          // eslint-disable-next-line no-param-reassign -- mutating the outgoing event is Sentry's documented beforeSend contract
          event.contexts = {
            ...event.contexts,
            vdom_storm: {
              started_at: new Date(storm.startedAt).toISOString(),
              age_ms: storm.ageMs,
              count: storm.count,
              first_message: storm.firstMessage,
              preceding_error_message: storm.precedingError?.message,
              preceding_error_info: storm.precedingError?.info,
              preceding_error_ms_before: storm.precedingError?.msBeforeStorm,
              nav_from: storm.navigation?.from,
              nav_to: storm.navigation?.to,
              nav_ms_before: storm.navigation?.msBeforeStorm
            }
          }
        }
      }

      const count = (errorCounts.get(errorMessage) || 0) + 1
      errorCounts.set(errorMessage, count)

      if (count > ERROR_THRESHOLD) {
        return null
      }

      return event
    },
    ...CUSTOM_CONFIG
  })

  // Sentry's Vue integration attached its errorHandler during init; wrap it so
  // the corruption tracker sees EVERY Vue-pipeline error. Recording cannot live
  // in beforeSend: sampleRate drops 99.5% of events before beforeSend runs, and
  // the storm trigger would almost never be the sampled one.
  const sentryErrorHandler = app.config.errorHandler
  // eslint-disable-next-line no-param-reassign -- wrapping the Vue error handler requires reassigning app.config.errorHandler, exactly like Sentry's own Vue integration does
  app.config.errorHandler = (err, instance, info) => {
    recordVueError(err instanceof Error ? err.message : String(err), info)
    if (sentryErrorHandler) {
      sentryErrorHandler(err, instance, info)
    } else {
      // Sentry's Vue integration always attaches a handler during init, so this
      // path should be unreachable — but never swallow an error silently.
      console.error(err)
    }
  }

  router.beforeEach((to, from) => {
    recordNavigation(from.fullPath, to.fullPath)
  })
}

export default initSentry
